import { Component } from '@angular/core';
import { Item, ItemGroup } from '../../domain/inventory.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { debounceTime, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { InventoryService } from '../../services/inventory.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItemService } from '../../services/item.service';
import { MessageHomeService } from '../../services/message-home.service';

@Component({
    selector: 'articles-release',
    templateUrl: './articles-release.component.html',
    styleUrl: './articles-release.component.scss',
    providers: [ConfirmationService],
    standalone: false
})
export class ArticlesReleaseComponent {
  scannedItems: Item[] = [];
  warehouse: string = '';
  accountId: number = 0;
  accountName: string = '';
  itemGroupId: number = 0;
  release: boolean = false;
  private articleSearchSubject = new Subject<string>();
  manualQrCode: string = '';
  isNewGroup: boolean = false;

  //Deactivation variables
  selectedItem: any = null;
  deactivationQuantity: number = 1;
  deactivationDialogVisible: boolean = false;
  releaseForm: FormGroup;
  releaseTypeOptions = [
    { label: 'Temporal', value: 'TEMPORAL' },
    { label: 'Permanente', value: 'PERMANENTE' }
  ];
  showTemporalField: boolean = true;

  messages: { severity: 'info'; detail: string }[] = [
    { severity: 'info', detail: 'Todos los artículos de este grupo serán dados de baja permanentemente al confirmar.' },
  ];

  constructor(private messageService: MessageService,
    private inventoryService: InventoryService, private route: ActivatedRoute, private router: Router, private confirmationService: ConfirmationService,
    private fb: FormBuilder, private itemService: ItemService, private messageHomeService: MessageHomeService
  ) {
    //this.warehouse = this.route.snapshot.paramMap.get('warehouse') || '';
    //this.accountId = Number(this.route.snapshot.paramMap.get('accountId'));
    this.articleSearchSubject.pipe(
      debounceTime(1000)
    ).subscribe(result => {
      this.onArticleScanSuccess(result);
    });
    this.releaseForm = this.fb.group({
      releaseType: ['TEMPORAL'],
      groupName: [''],
      exitDate: [new Date()],
      note: ['']
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.warehouse = params['warehouse'] || '';
      this.accountName = params['accountName'] ? params['accountName'] : '';
      this.accountId = params['accountId'] ? Number(params['accountId']) : 0;
      this.itemGroupId = params['itemGroupId'] ? Number(params['itemGroupId']) : 0;
      this.isNewGroup = this.itemGroupId ? false : true;
      this.loadGroupItems();
      this.validateForm();
    });
  }

  validateForm() {
    this.releaseForm.get('releaseType')?.valueChanges.subscribe(value => {
      if (value === 'TEMPORAL') {
        this.releaseForm.get('groupName')?.setValidators(Validators.required);
        this.releaseForm.get('note')?.clearValidators();
        this.showTemporalField = true;
      } else {
        this.releaseForm.get('groupName')?.clearValidators();
        this.releaseForm.get('note')?.setValidators(Validators.required);
        this.showTemporalField = false;
      }
      this.releaseForm.get('groupName')?.updateValueAndValidity();
      this.releaseForm.get('note')?.updateValueAndValidity();
    });
  }
  loadGroupItems() {
    if (!this.isNewGroup) {
      this.itemService.getItemsFromGroup(this.itemGroupId).subscribe({
        next: (itemGroup: ItemGroup) => {
          this.scannedItems = itemGroup.items;
          this.releaseForm.patchValue({
            releaseType: 'TEMPORAL',
            groupName: itemGroup.name,
            exitDate: new Date(itemGroup.exitDate),
            note: itemGroup.note
          });
          //this.releaseForm.get('exitDate')?.disable();
          this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Grupo Cargado' });
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error.message });
        }
      });
    }
  }

  onManualInputChange() {
    this.articleSearchSubject.next(this.manualQrCode);
  }

  openCamera() {
    const cameraInput = document.getElementById('cameraInput') as HTMLInputElement;
    cameraInput.click();
  }

  onArticleScan(qrCode: string) {
    this.articleSearchSubject.next(qrCode);
  }

  onArticleScanSuccess(qrCode: string) {
    this.manualQrCode = '';
    this.playSound();
    if (this.scannedItems.some(item => item.code === qrCode)) {
      this.messageService.add({ severity: 'warn', summary: 'Código duplicado', detail: 'Este código ya esta en la lista' });
      return;
    }

    this.inventoryService.getExistingItemInGroup(this.warehouse, this.accountId, qrCode, this.itemGroupId).subscribe((item: Item) => {
      if (!item) return;

      if (item.quantity === 0) {
        this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'Artículo sin cantidad disponible' });
        return;
      }

      if (item.quantity === 1) {
        this.messageService.add({ severity: 'success', summary: 'Escaneado', detail: 'Artículo agregado' });
        this.scannedItems.push(item);
      } else {
        this.openDeactivationDialog(item);
      }
    }, error => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error.message });
    });
  }

  openDeactivationDialog(item: any) {
    this.selectedItem = item;
    this.deactivationQuantity = 1;
    this.deactivationDialogVisible = true;
  }

  validateQuantity() {
    if (this.deactivationQuantity > this.selectedItem.quantity) {
      this.deactivationQuantity = this.selectedItem.quantity;
    }
    if (this.deactivationQuantity < 1) {
      this.deactivationQuantity = 1;
    }
  }

  confirmDeactivation() {
    this.selectedItem.quantity = this.deactivationQuantity;
    this.scannedItems.push(this.selectedItem);
    this.deactivationDialogVisible = false;
    this.messageService.add({ severity: 'success', summary: 'Escaneado', detail: 'Artículo agregado' });
  }

  playSound() {
    const audio = new Audio();
    audio.src = '../../assets/sounds/beepSound2.mp3';
    audio.load();
    audio.play();
  }

  removeItem(index: number) {
    this.scannedItems.splice(index, 1);
  }

  onClose() {
    this.confirmationService.confirm({
      message: '¿Quieres cancelar la Salida?',
      header: 'Confirmación',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.router.navigate(['/mobile/menu']);
        //this.location.back();
      }
    });
  }


  onReleaseBack(): void {
    this.release = false;
  }

  onRelease(): void {
    this.release = true;
  }

  onReleaseConfirm(): void {
    if (this.releaseForm.valid) {
      const releaseRequest = {
        ...this.releaseForm.getRawValue(),
        items: this.scannedItems,
        accountId: this.accountId,
        warehouse: this.warehouse,
        itemGroupId: this.itemGroupId
      };
      this.itemService.releaseItems(releaseRequest).subscribe({
        next: () => {
          this.messageHomeService.showSuccessAndNavigate(
            this.router,
            '/mobile/menu',
            'Salida completada',
            'Baja de Artículos Completada'
          );
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error.message, life: 5000 });
        }
      });
    } else {
      this.releaseForm.markAllAsTouched();
    }
  }

  get getReleaseType() {
    return this.releaseForm.get('releaseType')?.value;
  }

}